import {
  ReportDashboardKpis,
  ReportDefinitionRecord,
  ReportRunRecord,
  ReportSnapshotRecord,
  ReportExportRecord,
  ReportScheduleRecord,
  RegulatoryReportDefinition,
  RegulatoryReportSubmission,
  ReportTemplateRecord,
} from './types';

const API_BASE = '/api/reports';

export const reportsService = {
  async getDashboardKpis(): Promise<ReportDashboardKpis> {
    const res = await fetch(`${API_BASE}/dashboard/kpis`);
    if (!res.ok) throw new Error('Failed to fetch report KPIs');
    return res.json();
  },

  async getDefinitions(filters?: { family?: string; status?: string; search?: string }): Promise<ReportDefinitionRecord[]> {
    const query = new URLSearchParams();
    if (filters?.family) query.append('family', filters.family);
    if (filters?.status) query.append('status', filters.status);
    if (filters?.search) query.append('search', filters.search);

    const res = await fetch(`${API_BASE}/definitions?${query.toString()}`);
    if (!res.ok) throw new Error('Failed to fetch report definitions');
    return res.json();
  },

  async createDefinition(data: {
    report_code: string;
    family: string;
    title: string;
    description?: string;
    category?: string;
    owner_name?: string;
  }): Promise<ReportDefinitionRecord> {
    const res = await fetch(`${API_BASE}/definitions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Failed to create report definition');
    }
    return res.json();
  },

  async executeRun(
    reportId: string,
    data: {
      executed_by: string;
      parameters?: any;
    }
  ): Promise<{ run: ReportRunRecord; snapshot: ReportSnapshotRecord; exportRecord: ReportExportRecord }> {
    const res = await fetch(`${API_BASE}/definitions/${reportId}/run`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Failed to execute report run');
    }
    return res.json();
  },

  async getRuns(reportId?: string): Promise<ReportRunRecord[]> {
    const url = reportId ? `${API_BASE}/runs?report_id=${encodeURIComponent(reportId)}` : `${API_BASE}/runs`;
    const res = await fetch(url);
    if (!res.ok) throw new Error('Failed to fetch report runs');
    return res.json();
  },

  async getSnapshots(): Promise<ReportSnapshotRecord[]> {
    const res = await fetch(`${API_BASE}/snapshots`);
    if (!res.ok) throw new Error('Failed to fetch snapshots');
    return res.json();
  },

  async getExports(): Promise<ReportExportRecord[]> {
    const res = await fetch(`${API_BASE}/exports`);
    if (!res.ok) throw new Error('Failed to fetch exports');
    return res.json();
  },

  async getSchedules(): Promise<ReportScheduleRecord[]> {
    const res = await fetch(`${API_BASE}/schedules`);
    if (!res.ok) throw new Error('Failed to fetch schedules');
    return res.json();
  },

  async createSchedule(data: {
    report_id: string;
    name: string;
    recurrence_rule: string;
    recipients: string[];
    created_by: string;
  }): Promise<ReportScheduleRecord> {
    const res = await fetch(`${API_BASE}/schedules`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Failed to create report schedule');
    }
    return res.json();
  },

  async getRegulatoryDefinitions(): Promise<RegulatoryReportDefinition[]> {
    const res = await fetch(`${API_BASE}/regulatory/definitions`);
    if (!res.ok) throw new Error('Failed to fetch regulatory definitions');
    return res.json();
  },

  async getRegulatorySubmissions(): Promise<RegulatoryReportSubmission[]> {
    const res = await fetch(`${API_BASE}/regulatory/submissions`);
    if (!res.ok) throw new Error('Failed to fetch regulatory submissions');
    return res.json();
  },

  async createRegulatorySubmission(data: {
    definition_id: string;
    submission_number: string;
    reporting_period_start?: string;
    reporting_period_end?: string;
    prepared_by: string;
  }): Promise<RegulatoryReportSubmission> {
    const res = await fetch(`${API_BASE}/regulatory/submissions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Failed to create regulatory submission');
    }
    return res.json();
  },

  async submitToAuthority(
    id: string,
    data: {
      approved_by: string;
      external_reference: string;
    }
  ): Promise<RegulatoryReportSubmission> {
    const res = await fetch(`${API_BASE}/regulatory/submissions/${id}/submit`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Failed to submit regulatory report');
    }
    return res.json();
  },

  async getTemplates(): Promise<ReportTemplateRecord[]> {
    const res = await fetch(`${API_BASE}/templates`);
    if (!res.ok) throw new Error('Failed to fetch templates');
    return res.json();
  },
};
